// Lances da partida, tirados dos eventos do motor (@truco6/regras) enquanto a mesa joga. O estado é dado puro e
// fica no estado da sala, para sobreviver a deploy. Quem roda a mesa passa a mão de quem pede truco (para o blefe)
// e avisa quando alguém tocou "Aceitar mesmo assim" (para o Peito de Aço).
import { SEATS, rankOf, suitOf, team } from '@truco6/regras';
import type { Card, GameEvent, Rank, Seat, Special, Team } from '@truco6/regras';

export const LANCES = ['zap_rodada', 'zap_no_doze', 'truco_ganho', 'blefe', 'peito_de_aco', 'mao_de_ferro', 'virada'] as const;
// Feitos que só contam para as missões (item 13), sem Rodagem e sem badge: o trio ganhou uma mão valendo Seis ou
// mais; passou um sinal.
export const FEITOS = ['mao_seis', 'sinal'] as const;
export type Lance = (typeof LANCES)[number] | (typeof FEITOS)[number];

export const NOME_DO_LANCE: Record<(typeof LANCES)[number], string> = {
  zap_rodada: 'Zap que ganha a rodada',
  zap_no_doze: 'Zap no Doze',
  truco_ganho: 'Truco aceito e ganho',
  blefe: 'Blefe Vencedor',
  peito_de_aco: 'Peito de Aço',
  mao_de_ferro: 'Mão de Ferro Vencida',
  virada: 'Virada',
};

export interface LanceNoLugar { lugar: Seat; lance: Lance }

export interface EstadoLances {
  valor: number; // quanto a mão vale agora
  manilha: Rank | null;
  especial: Special;
  pedido: { lugar: Seat; blefe: boolean } | null; // truco ou aumento esperando resposta
  aceitos: Seat[]; // quem teve o pedido aceito nesta mão
  zap: { lugar: Seat; valor: number } | null; // quem jogou o Zap aberto na rodada
  peito: Seat | null; // quem tocou "Aceitar mesmo assim" nesta mão
  desvantagem: [number, number]; // a maior desvantagem de cada time na partida
  porLugar: Partial<Record<Seat, Lance[]>>;
}

export function novoRastreio(): EstadoLances {
  return { valor: 1, manilha: null, especial: null, pedido: null, aceitos: [], zap: null, peito: null, desvantagem: [0, 0], porLugar: {} };
}

// "Sem manilha e sem 3 na mão": a mão de quem pede, na hora do pedido.
export function maoDeBlefe(mao: readonly Card[], manilha: Rank | null): boolean {
  return !mao.some((c) => rankOf(c) === '3' || rankOf(c) === manilha);
}

function marcar(e: EstadoLances, novos: LanceNoLugar[], lugar: Seat, lance: Lance): void {
  (e.porLugar[lugar] ??= []).push(lance);
  novos.push({ lugar, lance });
}

const doTime = (t: Team) => SEATS.filter((s) => team(s) === t);

// Peito de Aço: votou aceitar contra os dois parceiros e tocou "Aceitar mesmo assim" no truco.
export function aceitouMesmoAssim(e: EstadoLances, lugar: Seat): void {
  e.peito = lugar;
}

// Um evento do motor. maoDeQuemPede: as cartas na mão de quem pediu truco ou aumentou, na hora do pedido.
export function observar(e: EstadoLances, ev: GameEvent, maoDeQuemPede?: readonly Card[]): LanceNoLugar[] {
  const novos: LanceNoLugar[] = [];
  switch (ev.type) {
    case 'hand_start':
      Object.assign(e, { valor: 1, manilha: ev.manilha, especial: ev.special, pedido: null, aceitos: [], zap: null, peito: null });
      break;
    case 'onze_play':
      e.valor = 3;
      break;
    case 'play':
      if (!ev.hidden && e.manilha && rankOf(ev.card) === e.manilha && suitOf(ev.card) === 'c') e.zap = { lugar: ev.seat, valor: e.valor };
      break;
    case 'round_end':
      if (e.zap && ev.result !== 'tie' && ev.winnerSeat === e.zap.lugar) {
        marcar(e, novos, e.zap.lugar, 'zap_rodada');
        if (e.zap.valor === 12) marcar(e, novos, e.zap.lugar, 'zap_no_doze');
      }
      e.zap = null;
      break;
    case 'truco':
      e.pedido = { lugar: ev.seat, blefe: maoDeBlefe(maoDeQuemPede ?? ['3c'], e.manilha) };
      break;
    case 'raise':
      // Aumentar aceita o pedido de antes e faz um pedido novo.
      if (e.pedido) e.aceitos.push(e.pedido.lugar);
      e.valor = ev.accepted;
      e.pedido = { lugar: ev.seat, blefe: maoDeBlefe(maoDeQuemPede ?? ['3c'], e.manilha) };
      break;
    case 'accept':
      e.valor = ev.level;
      if (e.pedido) e.aceitos.push(e.pedido.lugar);
      e.pedido = null;
      break;
    case 'run':
      if (e.pedido?.blefe) marcar(e, novos, e.pedido.lugar, 'blefe');
      e.pedido = null;
      break;
    case 'signal':
      marcar(e, novos, ev.seat, 'sinal');
      break;
    case 'hand_end': {
      const t = ev.team;
      if (t !== null) {
        for (const s of new Set(e.aceitos)) if (team(s) === t) marcar(e, novos, s, 'truco_ganho');
        if (e.peito !== null && team(e.peito) === t) marcar(e, novos, e.peito, 'peito_de_aco');
        if (e.especial === 'ferro') for (const s of doTime(t)) marcar(e, novos, s, 'mao_de_ferro');
        if (e.valor >= 6) for (const s of doTime(t)) marcar(e, novos, s, 'mao_seis');
      }
      for (const time of [0, 1] as const) e.desvantagem[time] = Math.max(e.desvantagem[time], ev.scores[1 - time]! - ev.scores[time]!);
      e.pedido = null; e.peito = null;
      break;
    }
    case 'game_over':
      if (e.desvantagem[ev.team] >= 8) for (const s of doTime(ev.team)) marcar(e, novos, s, 'virada');
      break;
  }
  return novos;
}
